import cx from 'clsx';
import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';
import TooltipStatus from '@components/common/StatusTypeItem/TooltipStatus';
import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { siteEngagementTooltipIds } from '@pages/SiteEngagement/constants/siteEngagementTooltipIds';
import { getFormattedDate } from '@pages/SiteEngagement/helpers/getFormattedDate';
import { getTimeBetween } from '@pages/SiteEngagement/helpers/getTimeBetween';
import type { IEngagementStatsActionItem } from '@pages/SiteEngagement/model/engagementStatsActionList';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import TableEngagementActionItemsSkeleton from './TableEngagementActionItemsSkeleton';
import style from './style.module.scss';

interface IProps {
  data: IEngagementStatsActionItem[] | undefined;
  sorting: ITableSorting;
  isLoading: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  hasNextPage: boolean;
  engagementActionItemsTableContainerRef: React.LegacyRef<HTMLDivElement>;
  onChangeSorting: (field: string) => void;
  fetchNextPage: () => void;
}

const TableEngagementActionItems: React.FC<IProps> = ({
  data,
  sorting,
  isLoading,
  isFetching,
  isRefetching,
  hasNextPage,
  engagementActionItemsTableContainerRef,
  onChangeSorting,
  fetchNextPage,
}) => {
  const { t } = useTranslation();

  const { customerId, locationId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const locationSelectValue = selectAppLocationSelectValue();

  const { data: profileData } = useUserProfileQuery();

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, hasNextPage, isFetching]);

  const isSelectedAllFacilitiesOrGroups =
    locationSelectValue.id === 'all' || locationSelectValue.level_name === 'group';
  const isTableLoading = isLoading || isRefetching;

  return (
    <div className={style.engagementTableWrapper} ref={engagementActionItemsTableContainerRef}>
      <table className={style.engagementTable}>
        <thead>
          <tr>
            <th className={style.nameColumn} title={t('Location')}>
              {t('Location')}
              <TableSort
                className={cx(style.engagementTableSortIcon, {
                  [style.activeSort]: sorting.id === 'location_name',
                })}
                onClick={() => onChangeSorting('location_name')}
                data-tooltip-content="location_name"
                data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
              />
            </th>
            <th>
              <div className={style.sortColumnWrapper}>
                {t('Title')}
                <span className={style.smallWidthShow}>/{t('Type')} </span>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'title',
                  })}
                  onClick={() => onChangeSorting('title')}
                  data-tooltip-content="title"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
            <th>
              <div className={cx(style.sortColumnWrapper, style.ellipsisTextThreeLines)}>
                <div>
                  {t('Status')}
                  <span className={style.smallWidthShow}>
                    /{t('Age')} ({t('Days')})
                  </span>
                </div>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'state_name',
                  })}
                  onClick={() => onChangeSorting('state_name')}
                  data-tooltip-content="state_name"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
            <th>
              <div className={cx(style.sortColumnWrapper, style.ellipsisTextThreeLines)}>
                <div>
                  {t('Equipment')}
                  <span className={style.smallWidthShow}>/{t('Component')}</span>
                </div>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'equipment_info',
                  })}
                  onClick={() => onChangeSorting('equipment_info')}
                  data-tooltip-content="equipment_info"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
            <th className={style.smallWidthHide}>{t('Component')}</th>
            <th className={style.smallWidthHide}>
              {t('Urgency')}
              <TableSort
                className={cx(style.engagementTableSortIcon, {
                  [style.activeSort]: sorting.id === 'urgency',
                })}
                onClick={() => onChangeSorting('urgency')}
                data-tooltip-content="urgency"
                data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
              />
            </th>
            <th className={style.smallWidthHide}>
              {t('Type')}
              <TableSort
                className={cx(style.engagementTableSortIcon, { [style.activeSort]: sorting.id === 'type' })}
                onClick={() => onChangeSorting('type')}
                data-tooltip-content="type"
                data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
              />
            </th>
            <th className={cx(style.alignRightColumn, style.smallWidthHide)}>
              <div className={style.sortColumnWrapper}>
                <div
                  className={style.ellipsisTextThreeLines}
                  data-tooltip-content={t(
                    'The number of days since the action item was created. Note that the date range beneath the day count represents the creation date and the date of the latest update/completion report.',
                  )}
                  data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Age')} ({t('Days')})
                </div>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'lifetime',
                  })}
                  onClick={() => onChangeSorting('lifetime')}
                  data-tooltip-content="lifetime"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
            <th className={style.alignRightColumn}>
              <div
                className={style.ellipsisTextThreeLines}
                data-tooltip-content={t(
                  'The total number of days during which Waites was awaiting a response from the site (that is, the days the action item was in the “New“, “Needs Response“, and “Awaiting Update“ statuses).',
                )}
                data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
              >
                {t('Time Awaiting Site Response')} ({t('Days')})
              </div>
            </th>
            <th className={style.alignRightColumn}>
              <div className={style.sortColumnWrapper}>
                <span
                  className={style.ellipsisTextThreeLines}
                  data-tooltip-content={t(
                    'The average number of days the action item was in the “New“, “Needs Response“, or “Awaiting Update“ statuses before a customer response or completion report.',
                  )}
                  data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Site response')} ({t('Days')})
                </span>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'customer_average_response',
                  })}
                  onClick={() => onChangeSorting('customer_average_response')}
                  data-tooltip-content="customer_average_response"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
            <th className={style.alignRightColumn}>
              <div className={style.sortColumnWrapper}>
                <span
                  className={style.ellipsisTextThreeLines}
                  data-tooltip-content={t(
                    'The number of days the action item was in the “New” status before there was a response.',
                  )}
                  data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                >
                  {t('Initial response')}, ({t('Days')})
                </span>
                <TableSort
                  className={cx(style.engagementTableSortIcon, {
                    [style.activeSort]: sorting.id === 'days_after_first_customer_reaction',
                  })}
                  onClick={() => onChangeSorting('days_after_first_customer_reaction')}
                  data-tooltip-content="days_after_first_customer_reaction"
                  data-tooltip-id={siteEngagementTooltipIds.SORT_TOOLTIP_ID}
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {isTableLoading ? (
            <TableEngagementActionItemsSkeleton />
          ) : data?.length ? (
            data.map((row) => (
              <tr
                key={row.id}
                className={style.engagementBodyRow}
                onClick={() => {
                  navigate(`/customer/${customerId}/ai/${row.id}`, {
                    state: {
                      locationId: locationId,
                      prevPage: pathname,
                    },
                  });
                }}
              >
                <td>
                  <div className={style.ellipsisTextThreeLines}>{row.location_name}</div>
                  {isSelectedAllFacilitiesOrGroups && (
                    <div className={style.additionalField}>{row.facility_name}</div>
                  )}
                </td>
                <td>
                  <div
                    className={style.ellipsisTextThreeLines}
                    data-tooltip-content={row.title}
                    data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                  >
                    {row.title}
                    <span className={cx(style.smallWidthShow, style.additionalField)}>
                      {t(row.type === 'waitesWireless' ? 'Hardware' : 'Regular')}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={style.statusTypeItemContainer}>
                    <StatusTypeItem
                      item={{
                        stateName: row.stateName === 'Closed' ? 'Archived' : row.stateName,
                        urgencyName: row.urgencyName,
                      }}
                      shortVariant={true}
                      tooltipId={siteEngagementTooltipIds.STATUS_TOOLTIP_ID}
                    />
                  </div>
                  <div className={cx(style.smallWidthShow, style.additionalField)}>
                    {getTimeBetween(row.age, row.lifetime, t)}
                    <div className={style.lifetimeRange}>
                      {getFormattedDate(
                        row.time_created,
                        (row.stateName === 'Closed' || row.stateName === 'Archived') && row.closedAt
                          ? row.closedAt
                          : row.updated_at,
                        profileData?.dateFormat.date,
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className={style.equipmentInfo}>
                    <div
                      className={style.ellipsisTextThreeLines}
                      data-tooltip-content={row.equipment_info}
                      data-tooltip-id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID}
                    >
                      {row.equipment_info}
                    </div>
                    <div className={cx(style.smallWidthShow, style.additionalField)}>{row.point_name}</div>
                  </div>
                </td>
                <td className={style.smallWidthHide}>{row.point_name}</td>
                <td className={style.smallWidthHide}>{row.urgencyName}</td>
                <td className={style.smallWidthHide}>
                  {t(row.type === 'waitesWireless' ? 'Hardware' : 'Regular')}
                </td>
                <td className={cx(style.alignRightColumn, style.smallWidthHide)}>
                  {getTimeBetween(row.age, row.lifetime, t)}
                  <div className={cx(style.additionalField, style.lifetimeRange)}>
                    {getFormattedDate(
                      row.time_created,
                      (row.stateName === 'Closed' || row.stateName === 'Archived') && row.closedAt
                        ? row.closedAt
                        : row.updated_at,
                      profileData?.dateFormat.date,
                    )}
                  </div>
                </td>
                <td className={style.alignRightColumn}>{row.customer_response_time}</td>
                <td className={style.alignRightColumn}>{row.customer_average_response}</td>
                <td className={style.alignRightColumn}>{row.days_after_first_customer_reaction}</td>
              </tr>
            ))
          ) : (
            <TableEmptyRowExtended text={t('No action items were found')} />
          )}
        </tbody>
        <tfoot>
          <TableLoadingTrigger show={!isFetching} ref={ref} />
          <TableLoading show={isFetching && !isLoading} />
        </tfoot>
      </table>
      <TooltipSort id={siteEngagementTooltipIds.SORT_TOOLTIP_ID} sorting={sorting} />
      <TooltipStatus id={siteEngagementTooltipIds.STATUS_TOOLTIP_ID} />
      <Tooltip id={siteEngagementTooltipIds.LONG_TEXT_TOOLTIP_ID} className={style.tooltipSizeMedium} />
    </div>
  );
};

export default TableEngagementActionItems;
